import { useMemo, useState } from "react";
import { Box, Group, Stack, Table, Text, Tooltip } from "@mantine/core";
import IconInfoCircle from "@tabler/icons-react/dist/esm/icons/IconInfoCircle.mjs";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import classes from "./RuleHistoryTable.module.scss";
import { EmptyState } from "../../../../../components/EmptyState/EmptyState";
import SortIcon from "../../../../../components/searchOptions/SortIcon";
import { SORT_DIRECTION } from "../../../../../utils/vars";

/**
 * History table inside the Details popup. Each row links to that rule
 * version's dashboard page (preserves legacy version-jumping
 * behavior); `currentRuleId` highlights the active row.
 */
export default function RuleHistoryTable({ history, currentRuleId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sort, setSort] = useState({ type: "DATE", direction: SORT_DIRECTION.DESC });

  const sortedHistory = useMemo(() => {
    if (!history) return history;
    return [...history].sort((a, b) => {
      const diff = new Date(a.changeDate) - new Date(b.changeDate);
      return sort.direction === SORT_DIRECTION.ASC ? diff : -diff;
    });
  }, [history, sort]);

  const toggleDateSort = () => {
    setSort((prev) => ({
      type: "DATE",
      direction:
        prev.direction === SORT_DIRECTION.DESC
          ? SORT_DIRECTION.ASC
          : SORT_DIRECTION.DESC,
    }));
  };

  if (!history?.length) {
    return <EmptyState message="No history available" />;
  }

  return (
    <Box style={{ maxHeight: 400, overflow: "auto" }}>
      <Table horizontalSpacing="md" highlightOnHover stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("multipleSearch.detailsPopup.changedName")}</Table.Th>
            <Table.Th>
              <Group
                style={{ cursor: "pointer" }}
                onClick={toggleDateSort}
                gap={4}
                wrap="nowrap"
              >
                {t("multipleSearch.detailsPopup.changeDate")}
                <SortIcon col="DATE" sort={sort} />
              </Group>
            </Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.changeType")}</Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.activeSince")}</Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.period")}</Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.periodCount")}</Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.recipients")}</Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.version")}</Table.Th>
            <Table.Th>{t("multipleSearch.detailsPopup.user")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sortedHistory.map((row) => {
            const recipients = Array.isArray(row.recipients)
              ? row.recipients
              : [];
            return (
              <Table.Tr
                key={row.id}
                className={
                  row.ruleId === currentRuleId ? classes.currentRow : ""
                }
                onClick={() => navigate(`/rules/rule-dashboard/${row.ruleId}`)}
                style={{ cursor: "pointer" }}
              >
                <Table.Td>{row.ruleName}</Table.Td>
                <Table.Td>
                  {row.changeDate
                    ? dayjs(row.changeDate).format("M/D/YYYY, h:mm A")
                    : ""}
                </Table.Td>
                <Table.Td>{row.changeType}</Table.Td>
                <Table.Td>
                  {row.activeSince
                    ? dayjs(row.activeSince).format("M/D/YYYY")
                    : ""}
                </Table.Td>
                <Table.Td>{row.period}</Table.Td>
                <Table.Td>{row.periodCount}</Table.Td>
                <Table.Td>
                  {recipients.length > 0 && (
                    <Tooltip
                      multiline
                      label={
                        <Stack gap={2}>
                          {recipients.map((email, i) => (
                            <Text key={i} size="xs">
                              {email}
                            </Text>
                          ))}
                        </Stack>
                      }
                    >
                      <span
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "inline-flex", cursor: "default" }}
                      >
                        <IconInfoCircle color="gray" size={18} />
                      </span>
                    </Tooltip>
                  )}
                </Table.Td>
                <Table.Td>{row.version}</Table.Td>
                <Table.Td>{row.author?.name}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
}
